/**
 * Device fingerprint utility using ThumbmarkJS
 * Generates a stable device fingerprint for fraud detection
 */

import { getFingerprint } from 'thumbmarkjs';
import axios from 'axios';

let cachedFingerprint = null;

export async function getDeviceFingerprint() {
  if (cachedFingerprint) return cachedFingerprint;
  try {
    cachedFingerprint = await getFingerprint();
    return cachedFingerprint;
  } catch (e) {
    console.error('Fingerprint generation failed:', e);
    return null;
  }
}

const FINGERPRINT_STORAGE_KEY = 'fp_last_sent';
const FINGERPRINT_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Send fingerprint to backend with throttling and retry.
 * Fire-and-forget: any failure (5xx, network, timeout) is fully silent —
 * fingerprinting is best-effort fraud detection, never user-visible.
 * Only sends if not sent recently (30 min window).
 * Returns true if sent (or already fresh), false on failure.
 */
export async function sendFingerprintToBackend(apiBase) {
  const lastSent = sessionStorage.getItem(FINGERPRINT_STORAGE_KEY);
  if (lastSent && (Date.now() - parseInt(lastSent, 10)) < FINGERPRINT_THROTTLE_MS) {
    return true; // Already sent recently
  }

  const fingerprint = await getDeviceFingerprint();
  if (!fingerprint) return false;

  // Up to 3 attempts with exponential backoff (1s, 3s) to ride out transient
  // 502s from upstream proxies. `validateStatus: () => true` keeps axios from
  // throwing (and hence from logging the network error in DevTools).
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await axios.post(
        `${apiBase}/auth/fingerprint`,
        { fingerprint },
        { withCredentials: true, validateStatus: () => true, timeout: 8000 }
      );
      if (res.status >= 200 && res.status < 300) {
        sessionStorage.setItem(FINGERPRINT_STORAGE_KEY, Date.now().toString());
        return true;
      }
      // Non-2xx — only retry on 5xx; 4xx is permanent (401, 403, etc.)
      if (res.status < 500) return false;
    } catch {
      // Network error — fall through to retry
    }
    if (attempt < 2) {
      await new Promise(r => setTimeout(r, attempt === 0 ? 1000 : 3000));
    }
  }
  return false;
}
